
/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareEntity } from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';
import {RequiredPayPlans} from './OrganisationType';
import {channelDefState} from "./ChannelDefType";
import config from '../../common/config';
import { DEFAULT_CURRENCY } from '../locales';
import { FLOW_DEF_VERSION_ID_DEVELOPMENT } from './FlowDefVersionType';

export const BILLING_TYPE_TARIFF = "tariff";
export const BILLING_TYPE_INSTANCE = "instance";
export const BILLING_TYPE_INSTANCE_INVOCATION = "instance+invocation";
export const BILLING_TYPE_INVOCATION = "invocation";
export const BILLING_TYPE_FREE = "free";
export const BILLING_TYPE_HIDE = "hide";

export const BillingTypes = {
    [BILLING_TYPE_HIDE]: {
        name: "None (no price info)",
    },
    [BILLING_TYPE_FREE]: {
        name: "No payment",
    },
    [BILLING_TYPE_TARIFF]: {
        name: "Pay By Tariff",
    },
    [BILLING_TYPE_INSTANCE]: {
        name: "Pay By Flow",
    },
    [BILLING_TYPE_INVOCATION]: {
        name: "Pay By Invocation",
    },
    [BILLING_TYPE_INSTANCE_INVOCATION]: {
        name: "Pay By Flow + Invocation",
    },
};

export default declareEntity({
    entity: "flowDefs",
    graphQL: "FlowDef",
    name: "Flow Definition",
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    dynamoPrefix: 'def',
    storageType: 'schema',
    defaultOrder: ['name', 'type'],
    isPublic: true,
    defaultTitle: (data) => {
        return data['name'];
    },
    fields: (types) => { return {
        id: {
            name: "Flow Definition ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            isPublic: true,
            required: true,
            userEditable: false,
        },
        flowDefVersion: {
            name: "Flow Definition Version",
            to: () => types['flowDefVersions'],
            type: "relation",
            defaultValue: (flow) => {
                return flow['flowDefVersion_id'] || FLOW_DEF_VERSION_ID_DEVELOPMENT;
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        name: {
            name: "Name",
            trim: true,
            type: "string",
            isPublic: true,
            translatable: true,
            filterable: true,
            defaultValue: "",
        },
        title: {
            name: "Title",
            description: "Title used in user detail.",
            trim: true,
            type: "string",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        billingType: {
            name: "Billing type",
            isPublic: true,
            type: "enum",
            defaultValue: BILLING_TYPE_HIDE,
            values: BillingTypes,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        pricePerInvocation: {
            name: "Price per invocation",
            type: "number",
            isPublic: true,
            visibility: (data) => { return data['billingType'] === BILLING_TYPE_INVOCATION || data['billingType'] === BILLING_TYPE_INSTANCE_INVOCATION; },
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        tablePerInvocation: {
            name: "Table per invocation",
            type: "string",
            description: "If you fill Price per instanace and Table per invocation, it will be used both of them.",
            isPublic: true,
            visibility: (data) => { return data['billingType'] === BILLING_TYPE_INVOCATION || data['billingType'] === BILLING_TYPE_INSTANCE_INVOCATION; },
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        invocationName: {
            name: "Invocation name",
            type: "string",
            translatable: true,
            isPublic: true,
            visibility: (data) => { return data['billingType'] === BILLING_TYPE_INVOCATION || data['billingType'] === BILLING_TYPE_INSTANCE_INVOCATION; },
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        priceInstallation: {
            name: "Installation Price",
            type: "number",
            isPublic: true,
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        priceInstallationNext: {
            name: "Installation Price (another)",
            type: "number",
            isPublic: true,
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        priceSelfInstallation: {
            name: "Self Installation Price",
            type: "number",
            isPublic: true,
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        pricePerInstance: {
            name: "Price per instance",
            type: "number",
            isPublic: true,
            visibility: (data) => { return data['billingType'] === BILLING_TYPE_INSTANCE || data['billingType'] === BILLING_TYPE_INSTANCE_INVOCATION; },
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        consultantHours: {
            name: "Expected consultant hours",
            type: "number",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        consultantCommHours: {
            name: "Expected communication hours",
            type: "number",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        productCode: {
            name: "Product Code",
            type: "string",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        accountingCode: {
            name: "Accounting Code",
            type: "string",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        pricePerInstanceNext: {
            name: "Price per instance (more)",
            type: "number",
            isPublic: true,
            visibility: (data) => { return data['billingType'] === BILLING_TYPE_INSTANCE || data['billingType'] === BILLING_TYPE_INSTANCE_INVOCATION; },
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        minPrice: {
            name: "Minimum Price",
            type: "number",
            isPublic: true,
            visibility: (data) => { return data['billingType'] === BILLING_TYPE_INVOCATION || data['billingType'] === BILLING_TYPE_INSTANCE_INVOCATION; },
            addonAfter: DEFAULT_CURRENCY,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        priceText: {
            name: "Price text",
            type: "string",
            description: "Text showed to the user instead of computed text from price parameters. You can use pricePerInstance, pricePerInstanceNext, pricePerInvocation, priceInstallation, invocationName.",
            isPublic: true,
            translatable: true,
            visibility: (data) => { return data['billingType'] !== BILLING_TYPE_HIDE; },
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        metadata: {
            name: "Metadata",
            type: "object",
            userEditable: false,
            import: false,
            export: false,
            controllerFn: "getMetaData",
        },
        originalFlowDefVersion: {
            name: "Original Flow Definition Version",
            to: () => types['flowDefVersions'],
            type: "relation",
            export: false,
            userEditable: false,
            permission: Permission.PERM_DEVELOPER,
        },
        versionConfig: {
            name: "Version Config",
            type: "object",
            import: false,
            export: false,
            userEditable: false,
            permission: Permission.PERM_DEVELOPER,
        },
        upgradeStrategy: {
            name: "Upgrade Status",
            type: "enum",
            filterable: true,
            import: false,
            export: false,
            defaultValue: "auto-1-week",
            permission: Permission.PERM_DEVELOPER,
            values: {
                "auto-1-week": {
                    name: "Auto upgrade (1 week)",
                },
                "auto-2-week": {
                    name: "Auto upgrade (2 week)",
                },
                "auto-4-week": {
                    name: "Auto upgrade (4 week)",
                },
                "manually": {
                    name: "Only manual upgrade",
                },
                "very-careful": {
                    name: "Upgrade only on request",
                },
            },
        },
        hasChange: {
            name: "Has Change",
            type: "boolean",
            export: false,
            import: false,
            filterable: true,
            userEditable: false,
            isPublic: false,
            permission: Permission.PERM_DEVELOPER,
        },
        type: {
            name: "Type",
            trim: true,
            type: "string",
            isPublic: true,
            defaultValue: "",
            validations: {
                matchRegexp: /^[a-zA-Z0-9\.\-_]+$/,
            }
        },
        state: {
            name: "State",
            type: "enum",
            filterable: true,
            isPublic: true,
            defaultValue: "draft",
            values: channelDefState
        },
        visibility: {
            name: "Visibility",
            type: "enum",
            filterable: true,
            isPublic: true,
            defaultValue: "private",
            values: {
                "private": {
                    name: "Private",
                },
                "unlisted": {
                    name: "Unlisted",
                },
                "public": {
                    name: "Public",
                },
            }
        },
        popularity: {
            name: "Popularity",
            type: "int",
            defaultValue: 1,
            isPublic: true,
            import: false,
            export: false,
        },
        description: {
            name: "Description",
            type: "text",
            translatable: true,
            isPublic: true,
            defaultValue: ""
        },
        longDescription: {
            name: "Long Description",
            type: "html",
            translatable: true,
            isPublic: true,
            defaultValue: ""
        },
        tutorialLink: {
            name: "Tutorial Link",
            type: "url",
            isPublic: true,
            defaultValue: ""
        },
        documentationLink: {
            name: "Documentation Link",
            type: "url",
            isPublic: true,
            defaultValue: ""
        },
        techDocumentationLink: {
            name: "Technical Documentation Link",
            type: "url",
            isPublic: true,
            defaultValue: ""
        },
        globallyDisabled: {
            name: "Globaly Disabled (emergency)",
            type: "boolean",
            defaultValue: false
        },
        autobill: {
            name: "Should we bill flow immediately",
            type: "boolean",
            defaultValue: false,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        hideZeroBilling: {
            name: "Hide on invoice with zero price",
            type: "boolean",
            defaultValue: false,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        storeTransmissions: {
            name: "Store transmissions",
            type: "boolean",
            defaultValue: false,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        appliedDescription: {
            name: "Applied Description",
            type: "html",
            translatable: true,
            isPublic: true,
            defaultValue: ""
        },
        requiredPayPlan: {
            name: "Required Plan",
            type: "enum",
            isPublic: true,
            filterable: true,
            defaultValue: "-",
            values: RequiredPayPlans,
            visibility: (data) => { return data['billingType'] === BILLING_TYPE_TARIFF; },
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        productManager: {
            name: "Product manager",
            type: "relation",
            to: () => types['users'],
            filterable: true,
            export: false,
            import: false,
        },
        changeAuthor: {
            name: "Last change author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        changeAuthorName: {
            name: "Change Author Name",
            type: "string",
            userEditable: false,
        },
        doNotSendErrors: {
            name: "Do not send errors",
            type: "boolean",
            defaultValue: false,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        developerGroup: {
            name: "Developer",
            type: "relation",
            to: () => types['developerGroups'],
            defaultValue: config.DEVELOPER_DATIVERY,
            filterable: true,
            userEditable: true,
            required: true,
            isPublic: true,
        },
        extendedFrom: {
            name: "Extended from",
            type: "relation",
            to: () => types['flowDefs'],
            isPublic: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
		diagramHeight: {
			name: "Height of the diagram box",
			type: "int",
            isPublic: true,
            defaultValue: 500,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
		},
        frontendScripts: {
            name: "Frontend Scripts",
            type: "object",
            controllerFn: "getFrontendScripts",
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
    }; },
    relations: (types) => { return {
        flowOptionDefs: {
            type: 'oneToMany',
            to: types['flowOptionDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        flowDefDocs: {
            type: 'oneToMany',
            to: types['flowDefDocs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        flowCoordinationEvents: {
            type: 'oneToMany',
            to: types['flowCoordinationEvents'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        flowCoordinationHooks: {
            type: 'oneToMany',
            to: types['flowCoordinationHooks'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allFlowOptionDefs: {
            type: 'manyToOne',
            to: types['flowOptionDefs'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAllFlowOptionDefs',
        },
        allFlowOptionGroupDefs: {
            type: 'oneToMany',
            to: types['flowOptionGroupDefs'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAllFlowOptionGroupDefs',
        },
        flowOptionGroupDefs: {
            type: 'oneToMany',
            to: types['flowOptionGroupDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        flowBillingRules: {
            type: 'oneToMany',
            to: types['flowBillingRules'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        flowScriptDefs: {
            type: 'oneToMany',
            to: types['flowScriptDefs'],
            export: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            defaultValue: () => {
                return {};
            },
        },
        allFlowScriptDefs: {
            type: 'manyToOne',
            to: types['flowScriptDefs'],
            args: {
                id: {
                    type: "string"
                },
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            shouldFollow: false,
            controllerFn: 'getAllFlowScriptDefs',
        },
        flowChannelDefs: {
            type: 'oneToMany',
            to: types['flowChannelDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allFlowChannelDefs: {
            type: 'manyToOne',
            to: types['flowChannelDefs'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAllFlowChannelDefs',
        },
        flowStepDefs: {
            type: 'oneToMany',
            to: types['flowStepDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allFlowStepDefs: {
            type: 'manyToOne',
            to: types['flowStepDefs'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAllFlowStepDefs',
        },
        fetches: {
            type: 'oneToMany',
            to: types['fetches'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "flowDef_id"
                }
            ]
        },
        flowInvocations: {
            type: 'oneToMany',
            to: types['flowInvocations'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "flowDef_id"
                }
            ]
        },
        flows: {
            type: 'oneToMany',
            to: types['flows'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "flowDef_id"
                }
            ]
        },
        flowDefVersions: {
            type: 'oneToMany',
            to: types['flowDefVersions'],
            export: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "flowDef_id"
                }
            ]
        },
        flowStepConfigDefs: {
            type: 'oneToMany',
            to: types['flowStepConfigDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        allFlowStepConfigDefs: {
            type: 'manyToOne',
            to: types['flowStepConfigDefs'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAllFlowStepConfigDefs',
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        flowExtensionLinkDefs: {
            type: 'oneToMany',
            to: types['flowExtensionLinkDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allFlowExtensionLinkDefs: {
            type: 'manyToOne',
            to: types['flowExtensionLinkDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAllFlowExtensionLinkDefs',
        },
        forms: {
            type: 'oneToMany',
            to: types['forms'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        allForms: {
            type: 'manyToOne',
            to: types['forms'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAllForms',
        },
        logger: {
            type: 'oneToMany',
            to: types['loggers'],
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "flowDef_id"
                }
            ]
        },
        channelErrors: {
            type: 'oneToMany',
            to: types['channelErrors'],
            permission: Permission.PERM_INSTANCE_ADMIN,
            foreignKeys: [
                {
                    from: "id",
                    to: "flowDef_id"
                },
            ]
        },
    }; }
});
